import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Paper,
  Tab,
  Tabs,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useNavigate, Navigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { expenseUserStatusLabel, ticketAmountLine } from "./expenseUserI18n";
import {
  createExpenseUserDocument,
  listExpenseUserDocuments,
} from "../../api/ExpensesUser/expenseUserDocumentsApi";
import {
  getExpenseUserStatusChipProps,
  KIND_STAFF,
  KIND_SUPPLIER,
} from "./expenseUserStatusChips";
import {
  isCompanyCardTicket,
  ticketPaymentChipProps,
  ticketPaymentTypeChipProps,
} from "./expenseUserPaymentChips";
import { expenseUserPageShellSx } from "./expenseUserLayout";
import ExpenseUserWebcamDialog from "./ExpenseUserWebcamDialog";
import ExpensesUserTicketsMobile from "./ExpensesUserTicketsMobile";
import {
  pageFieldSx,
  pageHeroShellSx,
  pageContentCardSx,
} from "../../styles/pageModernStyles";

function hasPermisoVerTodoExpensesFromToken() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return false;
    const d = jwtDecode(t);
    const v = d?.permisoVertodoExpenses;
    if (v === true || v === 1) return true;
    if (typeof v === "string" && (v === "1" || v.toLowerCase() === "true")) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** En escritorio, `capture` hace que el navegador espere cámara y puede tardar minutos antes de abrir el diálogo. */
const shouldUseCameraCapture = () =>
  typeof navigator !== "undefined" &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || "",
  );

const FILTER_CONTAB = { ALL: "all", YES: "yes", NO: "no" };
const FILTER_PAY = { ALL: "all", PAID: "paid", PENDING: "pending" };
const FILTER_FIRMA = {
  ALL: "all",
  PENDING_SIGN: "pending_sign",
  SIGNED_EMPLOYEE: "signed_employee",
  SIGNED_SUPERVISOR: "signed_supervisor",
};

const ExpensesUserPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin, isMobile } = useContext(SidebarContext);

  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  /** Por defecto: pendientes de contabilizar (A3). */
  const [contabFilter, setContabFilter] = useState(FILTER_CONTAB.NO);
  const [paymentFilter, setPaymentFilter] = useState(FILTER_PAY.ALL);
  const [firmaFilter, setFirmaFilter] = useState(FILTER_FIRMA.ALL);
  const [searchDraft, setSearchDraft] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [webcamOpen, setWebcamOpen] = useState(false);
  const isSuperUser = hasPermisoVerTodoExpensesFromToken();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (tab === 2) {
        params.status = "posted_a3";
      } else {
        if (tab === 0) params.kind = KIND_STAFF;
        if (tab === 1) params.kind = KIND_SUPPLIER;
        if (contabFilter !== FILTER_CONTAB.ALL) {
          params.contabilizado = contabFilter;
        }
        if (paymentFilter !== FILTER_PAY.ALL) {
          params.payment = paymentFilter;
        }
        if (firmaFilter !== FILTER_FIRMA.ALL) {
          params.firma = firmaFilter;
        }
        if (searchApplied.trim()) {
          params.q = searchApplied.trim();
        }
      }
      const data = await listExpenseUserDocuments(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(
        e?.response?.data?.error || e.message || t("ExpenseUser.notes.errList"),
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab, contabFilter, paymentFilter, firmaFilter, searchApplied, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isSuperUser && tab !== 0) setTab(0);
  }, [isSuperUser, tab]);

  const uploadKind =
    tab === 0 ? KIND_STAFF : tab === 1 ? KIND_SUPPLIER : KIND_STAFF;

  const handleFiles = async (fileList, inputEl) => {
    const file = fileList?.[0];
    if (inputEl) inputEl.value = "";
    if (!file || tab === 2 || uploading) return;
    try {
      setUploading(true);
      setError(null);
      const doc = await createExpenseUserDocument(uploadKind, file);
      navigate(`/expenses-user/${doc.id}`);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errUpload"),
      );
    } finally {
      setUploading(false);
    }
  };

  const filterFieldSx = useMemo(() => pageFieldSx(theme), [theme]);

  if (!isSuperUser) {
    return <Navigate to="/expenses-user/notes" replace />;
  }

  return (
    <Box sx={expenseUserPageShellSx(contentMargin, { maxWidth: 1200 })}>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={() => navigate("/expenses-user/notes")}
        sx={{ mb: 1.5, alignSelf: "flex-start", borderRadius: 2 }}
      >
        {t("ExpenseUser.tickets.back")}
      </Button>

      <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Box sx={{ display: "flex", minWidth: 0 }}>
            <ForumOutlinedIcon
              sx={{
                color: "primary.main",
                fontSize: { xs: "2rem", sm: "2.25rem" },
                mr: 1.5,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1, display: "block", fontWeight: 700 }}
              >
                CRM
              </Typography>
              <Typography
                component="h1"
                variant="h5"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                {t("ExpenseUser.tickets.allTitle")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t("ExpenseUser.tickets.subtitle")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => {
          if (!isSuperUser && (v === 1 || v === 2)) return;
          setTab(v);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={t("ExpenseUser.tickets.tabStaff")} />
        <Tab
          label={t("ExpenseUser.tickets.tabSupplier")}
          disabled={!isSuperUser}
        />
        <Tab
          label={t("ExpenseUser.tickets.tabPosted")}
          disabled={!isSuperUser}
        />
      </Tabs>

      {tab !== 2 && (
        <Paper
          variant="outlined"
          sx={{
            ...pageContentCardSx(theme, theme.palette.primary.main),
            p: { xs: 1.25, sm: 2 },
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t("ExpenseUser.tickets.filtersTitle")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(6, 1fr)",
              },
              gap: 2,
              alignItems: "flex-end",
              mb: 2,
            }}
          >
            <FormControl size="small" fullWidth sx={filterFieldSx}>
              <InputLabel id="flt-contab">
                {t("ExpenseUser.tickets.contabA3")}
              </InputLabel>
              <Select
                labelId="flt-contab"
                label={t("ExpenseUser.tickets.contabA3")}
                value={contabFilter}
                onChange={(e) => setContabFilter(e.target.value)}
              >
                <MenuItem value={FILTER_CONTAB.ALL}>
                  {t("ExpenseUser.tickets.contabAll")}
                </MenuItem>
                <MenuItem value={FILTER_CONTAB.YES}>
                  {t("ExpenseUser.tickets.contabYes")}
                </MenuItem>
                <MenuItem value={FILTER_CONTAB.NO}>
                  {t("ExpenseUser.tickets.contabNo")}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={filterFieldSx}>
              <InputLabel id="flt-pay">
                {t("ExpenseUser.tickets.payment")}
              </InputLabel>
              <Select
                labelId="flt-pay"
                label={t("ExpenseUser.tickets.payment")}
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
              >
                <MenuItem value={FILTER_PAY.ALL}>
                  {t("ExpenseUser.tickets.payAll")}
                </MenuItem>
                <MenuItem value={FILTER_PAY.PAID}>
                  {t("ExpenseUser.tickets.payPaid")}
                </MenuItem>
                <MenuItem value={FILTER_PAY.PENDING}>
                  {t("ExpenseUser.tickets.payPending")}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={filterFieldSx}>
              <InputLabel id="flt-firma">
                {t("ExpenseUser.tickets.sign")}
              </InputLabel>
              <Select
                labelId="flt-firma"
                label={t("ExpenseUser.tickets.sign")}
                value={firmaFilter}
                onChange={(e) => setFirmaFilter(e.target.value)}
              >
                <MenuItem value={FILTER_FIRMA.ALL}>
                  {t("ExpenseUser.tickets.signAll")}
                </MenuItem>
                <MenuItem value={FILTER_FIRMA.PENDING_SIGN}>
                  {t("ExpenseUser.tickets.signPending")}
                </MenuItem>
                <MenuItem value={FILTER_FIRMA.SIGNED_EMPLOYEE}>
                  {t("ExpenseUser.tickets.signSignedEmp")}
                </MenuItem>
                <MenuItem value={FILTER_FIRMA.SIGNED_SUPERVISOR}>
                  {t("ExpenseUser.tickets.signSignedSup")}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label={t("ExpenseUser.tickets.searchLabel")}
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setSearchApplied(searchDraft);
                }
              }}
              sx={{ minWidth: 220, flex: "1 1 220px", ...filterFieldSx }}
            />
            <Button
              variant="contained"
              onClick={() => setSearchApplied(searchDraft)}
            >
              {t("ExpenseUser.tickets.searchBtn")}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                setContabFilter(FILTER_CONTAB.NO);
                setPaymentFilter(FILTER_PAY.ALL);
                setFirmaFilter(FILTER_FIRMA.ALL);
                setSearchDraft("");
                setSearchApplied("");
              }}
            >
              {t("ExpenseUser.tickets.resetDefaults")}
            </Button>
          </Box>
        </Paper>
      )}

      {tab === 2 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("ExpenseUser.tickets.postedInfo")}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tab !== 2 && (
        <Paper
          sx={{
            p: 3,
            mb: 2,
            border: "2px dashed",
            borderColor: dragActive ? "primary.main" : "divider",
            bgcolor: dragActive ? "action.hover" : "background.paper",
            textAlign: "center",
            position: "relative",
            opacity: uploading ? 0.9 : 1,
            pointerEvents: uploading ? "none" : "auto",
            borderRadius: 2,
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            handleFiles(e.dataTransfer.files, null);
          }}
        >
          {uploading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                bgcolor: "rgba(255, 255, 255, 0.75)",
                backdropFilter: "blur(2px)",
              }}
            >
              <CircularProgress size={30} />
              <Typography variant="body2" fontWeight={600}>
                {t("ExpenseUser.tickets.processing")}
              </Typography>
            </Box>
          )}
          <Typography variant="body1" sx={{ mb: 1, ml: 2 }}>
            {t("ExpenseUser.tickets.dragHere")}
          </Typography>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: "center",
            }}
          >
            <label
              htmlFor="expenses-user-pick-file"
              style={{ cursor: "pointer" }}
            >
              <input
                id="expenses-user-pick-file"
                type="file"
                hidden
                accept="image/*,application/pdf"
                onChange={(e) => handleFiles(e.target.files, e.target)}
              />
              <Button variant="contained" component="span">
                {t("ExpenseUser.chooseFile")}
              </Button>
            </label>
            {shouldUseCameraCapture() ? (
              <label htmlFor="expenses-user-camera" style={{ cursor: "pointer" }}>
                <input
                  id="expenses-user-camera"
                  type="file"
                  hidden
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFiles(e.target.files, e.target)}
                />
                <Button variant="outlined" component="span">
                  {t("ExpenseUser.takePhoto")}
                </Button>
              </label>
            ) : (
              <Button variant="outlined" onClick={() => setWebcamOpen(true)}>
                {t("ExpenseUser.takePhoto")}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {loading
          ? t("ExpenseUser.tickets.docCount")
          : t("ExpenseUser.tickets.docCountN", { count: rows.length })}
      </Typography>

      {isMobile ? (
        <ExpensesUserTicketsMobile
          theme={theme}
          rows={rows}
          t={t}
          onOpen={(docId) => navigate(`/expenses-user/${docId}`)}
        />
      ) : (
        rows.map((row) => (
          <Paper
            key={row.id}
            variant="outlined"
            sx={{
              ...pageContentCardSx(theme, theme.palette.primary.main),
              p: 2,
              mb: 1,
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => navigate(`/expenses-user/${row.id}`)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="body1">
                  #{row.id} ·{" "}
                  {row.OriginalFileName || t("ExpenseUser.note.noFileName")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("ExpenseUser.user")}:{" "}
                  {row.Owner?.username ||
                    row.Owner?.email ||
                    (row.UserId != null
                      ? `#${row.UserId}`
                      : t("ExpenseUser.emptyDash"))}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={
                    row.Kind === KIND_STAFF
                      ? t("ExpenseUser.kindPersonal")
                      : t("ExpenseUser.kindSupplier")
                  }
                />
                <Chip
                  size="small"
                  label={expenseUserStatusLabel(t, row.Status)}
                  {...getExpenseUserStatusChipProps(row.Status, row.Kind)}
                />
                <Chip
                  size="small"
                  {...ticketPaymentTypeChipProps(row, t)}
                />
                {!isCompanyCardTicket(row) && (
                  <Chip
                    size="small"
                    {...ticketPaymentChipProps(row.PaymentStatus, t)}
                  />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={ticketAmountLine(t, row)}
                />
                {row.A3Reference && (
                  <Chip
                    size="small"
                    color="success"
                    label={`A3: ${row.A3Reference}`}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        ))
      )}
      <ExpenseUserWebcamDialog
        open={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        onCapture={(file) => handleFiles([file], null)}
        t={t}
      />
    </Box>
  );
};

export default ExpensesUserPage;
